import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { TicketQR } from "../components/site/TicketQR";
import { api, apiError } from "../lib/api";
import { formatARS } from "../lib/site";

export default function PagoResultado() {
  const [sp] = useSearchParams();
  const code = sp.get("code") || "";
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!code) { setErr("Falta el código del pago."); return undefined; }
    if (d && d.status !== "pendiente_pago") return undefined;
    if (tries > 20) return undefined;
    const t = setTimeout(() => {
      api.get(`/payments/nave/status/${encodeURIComponent(code)}`).then((r) => setD(r.data)).catch((e) => setErr(apiError(e))).finally(() => setTries((n) => n + 1));
    }, tries === 0 ? 0 : 3000);
    return () => clearTimeout(t);
  }, [code, d, tries]);

  const ok = d?.status === "confirmado";
  const failed = d && ["vencida", "cancelado", "cambio_error"].includes(d.status);
  return (
    <section data-testid="payment-result-page" className="grid min-h-[70vh] place-items-center bg-[var(--paper)] px-5 py-16">
      <div className="w-full max-w-md rounded-[28px] bg-white p-8 text-center ring-1 ring-black/5">
        {err ? <p data-testid="payment-result-error" className="text-red-700">{err}</p>
          : !d || (d.status === "pendiente_pago" && tries <= 20) ? (
            <div data-testid="payment-pending"><Loader2 className="mx-auto h-10 w-10 animate-spin text-[var(--forest)]" /><p className="mt-4 font-display text-2xl">Confirmando tu pago…</p><p className="mt-2 text-sm text-[var(--muted-ink)]">No cierres esta página.</p></div>
          ) : ok ? (
            <div data-testid="payment-ok">
              <CheckCircle2 className="mx-auto h-14 w-14 text-[var(--forest)]" />
              <p className="mt-4 font-display text-3xl">{d.change ? "¡Cambio de horario pagado!" : d.legs?.length > 1 ? "¡Ida y vuelta pagadas!" : "¡Pasaje pagado!"}</p>
              {(d.legs?.length > 1 ? d.legs : [d]).map((x, n) => (
                <div key={x.code} data-testid={`payment-leg-${n}`} className="mt-5 border-t border-black/5 pt-4 first:border-0 first:pt-0">
                  <p className="text-sm text-[var(--muted-ink)]">{x.origin} → {x.destination} · {x.date} {x.departure} hs · butacas {(x.seats ?? []).join(", ")} · {d.change ? `cargo ${formatARS(x.total)}` : formatARS(x.total)}</p>
                  <p className="mt-3 font-mono text-xl font-bold tracking-[0.2em] text-[var(--forest)]">{x.code}</p>
                  <TicketQR code={x.code} className="mt-3 ring-1 ring-black/10" />
                  {x.ticket_url && <a href={x.ticket_url} data-testid={n ? `payment-ticket-link-${n}` : "payment-ticket-link"} className="mt-3 inline-block rounded-full bg-[var(--lime)] px-5 py-2.5 text-sm font-bold">Ver mi pasaje</a>}
                </div>
              ))}
              <p className="mt-4 text-xs text-[var(--muted-ink)]">Te enviamos {d.change ? "el nuevo horario" : "los pasajes"} por email. Mostrá el QR al conductor al subir.</p>
            </div>
          ) : (
            <div data-testid={failed ? "payment-failed" : "payment-unknown"}>
              <XCircle className="mx-auto h-14 w-14 text-red-700" />
              <p className="mt-4 font-display text-3xl">{failed ? "El pago no se completó" : "Todavía no recibimos la confirmación"}</p>
              <p className="mt-2 text-sm text-[var(--muted-ink)]">{d.status === "cambio_error" ? "El pago se acreditó pero ya no había lugar en ese horario: acercate a una boletería y lo resolvemos." : failed ? (d.change ? "Tu pasaje sigue en el horario original. Podés intentar de nuevo." : "Las butacas se liberaron. Podés intentar de nuevo.") : `Guardá tu código ${d.code}: si el pago se aprueba te llega el pasaje por email.`}</p>
            </div>
          )}
        <Link to="/pasajes" data-testid="payment-back" className="mt-6 inline-block rounded-full bg-[var(--ink)] px-6 py-3 text-sm font-bold text-white">Volver a pasajes</Link>
      </div>
    </section>
  );
}
